/**
 * Compliance audit document sources (CUR-2555): the JSON columns on `ComplianceAuditCollection`.
 *
 * Enums (provider, statuses, candidate states) are native Prisma enums in `@shared/prisma/enums`.
 * These schemas are read parsers as well as write contracts: call sites validate the stored JSON
 * with the parse helpers instead of casting it.
 */

import { z } from 'zod';

const MAX_ROW_ID = BigInt('9223372036854775807');

// A Curium row ID carried as a string, because JSON has no bigint: positive, within Postgres BIGINT
// and canonical (no leading zeros), so each row has one spelling and string comparison is ID comparison.
// Zod runs the refinement even when the pattern has already failed, so it tests the pattern itself.
const ROW_ID_PATTERN = /^[1-9]\d{0,18}$/;
const rowIdSchema = z
    .string()
    .refine((value) => ROW_ID_PATTERN.test(value) && BigInt(value) <= MAX_ROW_ID, 'must be a row ID');

// `ComplianceAuditCollection.query`: what one search looks for. The policy fields come from the
// audit; `extraTerms` are the officer's own. Every term is quoted and parenthesised before it
// reaches Microsoft Search, and the search scope is built from the approved libraries' stored
// list IDs, never from these strings.
export const complianceAuditCollectionQuerySchema = z
    .object({
        policyNumber: z.string().trim().min(1).max(100).nullable(),
        // As long as an audit's own insured name may be (`singleLineAuditIdentity(255)`).
        insuredName: z.string().trim().min(1).max(255).nullable(),
        // The policy term window. A folder named for a year inside it is a policy-term folder.
        termStart: z.iso.date().nullable(),
        termEnd: z.iso.date().nullable(),
        extraTerms: z.array(z.string().trim().min(1).max(100)).max(10),
        // The approved libraries this search covers (`DocumentSourceLibrary` IDs), each once.
        libraryIds: z
            .array(rowIdSchema)
            .min(1)
            .max(50)
            .refine((ids) => new Set(ids).size === ids.length, 'must name each library once'),
    })
    .refine((query) => !query.termStart || !query.termEnd || query.termStart <= query.termEnd, {
        path: ['termEnd'],
        message: 'must not be before termStart',
    });

export type ComplianceAuditCollectionQuery = z.infer<typeof complianceAuditCollectionQuerySchema>;

export function parseComplianceAuditCollectionQuery(value: unknown): ComplianceAuditCollectionQuery | null {
    const result = complianceAuditCollectionQuerySchema.safeParse(value);
    return result.success ? result.data : null;
}

// How a library was searched: by walking its folders, through Microsoft Search scoped to the
// library, or both.
export const COMPLIANCE_AUDIT_COLLECTION_STRATEGIES = ['CRAWL', 'SEARCH'] as const;

export type ComplianceAuditCollectionStrategy = (typeof COMPLIANCE_AUDIT_COLLECTION_STRATEGIES)[number];

const collectionLibrarySummarySchema = z.object({
    libraryId: rowIdSchema,
    name: z.string().max(500),
    // How it was searched: at least one strategy, each once.
    strategies: z
        .array(z.enum(COMPLIANCE_AUDIT_COLLECTION_STRATEGIES))
        .min(1)
        .max(2)
        .refine((strategies) => new Set(strategies).size === strategies.length, 'must name each strategy once'),
    candidateCount: z.number().int().min(0),
    // The crawl or search stopped at its limit, so the library may hold more matches.
    truncated: z.boolean(),
});

// `ComplianceAuditCollection.summary`: what was searched and what was not, so the officer can
// see the gaps. Written when discovery finishes.
export const complianceAuditCollectionSummarySchema = z.object({
    libraries: z
        .array(collectionLibrarySummarySchema)
        .max(50)
        .refine(
            (libraries) => new Set(libraries.map((library) => library.libraryId)).size === libraries.length,
            'must list each library once',
        ),
    // Search hits dropped because their drive or list was not an approved library's. Expected to
    // be zero; anything else means Microsoft answered outside the scope Curium asked for.
    outOfScopeHitCount: z.number().int().min(0),
    // Libraries on granted sites that the organisation has not approved: named, never searched.
    notApprovedLibraries: z.array(z.object({ libraryId: rowIdSchema, name: z.string().max(500) })).max(500),
    // Granted sites the connection could not read during this search.
    unreachableSites: z.array(z.object({ siteId: rowIdSchema, displayName: z.string().max(500) })).max(100),
});

export type ComplianceAuditCollectionSummary = z.infer<typeof complianceAuditCollectionSummarySchema>;

/**
 * Why a candidate was found (`ComplianceAuditCandidate.matchReasons`). The first three tick it in
 * the review by default.
 *
 * - `POLICY_FOLDER`: in the insured's folder for the policy year.
 * - `POLICY_NUMBER_IN_NAME`: its name carries the policy number, with or without separators.
 * - `SEARCH_POLICY_NUMBER`: Microsoft Search found the policy number in it.
 * - `INSURED_FOLDER`: somewhere in the insured's folder.
 * - `SEARCH_INSURED_NAME`: Microsoft Search found the insured's name in it.
 * - `SEARCH_OFFICER_TERM`: Microsoft Search found one of the officer's own terms in it.
 */
export const COMPLIANCE_AUDIT_CANDIDATE_MATCH_REASONS = [
    'POLICY_FOLDER',
    'POLICY_NUMBER_IN_NAME',
    'SEARCH_POLICY_NUMBER',
    'INSURED_FOLDER',
    'SEARCH_INSURED_NAME',
    'SEARCH_OFFICER_TERM',
] as const;

export type ComplianceAuditCandidateMatchReason = (typeof COMPLIANCE_AUDIT_CANDIDATE_MATCH_REASONS)[number];

/** The reasons that tick a candidate in the review by default. */
export const SUGGESTING_MATCH_REASONS: readonly ComplianceAuditCandidateMatchReason[] = [
    'POLICY_FOLDER',
    'POLICY_NUMBER_IN_NAME',
    'SEARCH_POLICY_NUMBER',
];

export function parseComplianceAuditCollectionSummary(value: unknown): ComplianceAuditCollectionSummary | null {
    const result = complianceAuditCollectionSummarySchema.safeParse(value);
    return result.success ? result.data : null;
}
